import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Axios from "axios";

import { getClipTitle } from "../Utilities/Clip";
import { getClient } from "../Utilities/statsigClient";
import { getLastAttributionId } from "MVVM/Utilities/Attribution";

declare global {
  interface Window {
    rudderanalytics: any;
    clarity: any;
  }
}

const track = (
  event: string,
  properties: Record<string, unknown> = {},
): void => {
  const statsigClient = getClient();
  window.rudderanalytics.track(event, {
    ...properties,
    statsigCustomIDs: ["stableID", statsigClient?.getContext()?.stableID],
    attributionId: getLastAttributionId(),
  });
};

const identify = (
  userId: string | null,
  traits?: Record<string, unknown>,
): void => {
  window.rudderanalytics.identify(userId, { ...traits });
};

export const analytics = {
  track,
  identify,
};

export const PageViewTracker = () => {
  const statsigClient = getClient();
  const location = useLocation();
  const prevPath = useRef<string>();

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      window.rudderanalytics.page({
        statsigCustomIDs: ["stableID", statsigClient?.getContext()?.stableID],
      });

      prevPath.current = location.pathname;
    }
  }, [location.pathname, statsigClient]);

  return null;
};

export const trackClipSocialShare = ({
  platform,
  handler,
  clip,
  queue,
  data,
  aspectRatio,
}: any) => {
  analytics.track("Clip Shared", {
    clipID: clip.internalId || clip._id || clip.shareId,
    game: clip.game,
    tags: clip.clipProperties,
    length: clip.clipLength,
    clipOwner: clip.user?._id,
    shareDestination: platform,
    shareId: clip?.shareId,
  });

  const analyticsProperties = {
    clipName: getClipTitle(clip) || clip.title,
    game: clip.game,
    tags: clip.clipProperties,
    length: clip.clipLength,
    total_reactions: clip.totalReactions,
    total_views: clip.views,
    shareDestination: platform,
    shareId: clip?.shareId,
  };

  const uploadPlatforms = ["YouTube", "TikTok"];

  if (handler && uploadPlatforms.includes(platform)) {
    const baseUrl = process.env.REACT_APP_API_URL;

    handler({
      method: "POST",
      url: `${baseUrl}/items/clip/upload${queue === true ? "?queue=true" : ""}`,
      data: {
        _id: clip.internalId || clip._id,
        platform,
        data,
        aspectRatio,
        analytics: analyticsProperties,
      },
    });
  } else {
    postApiSegment({
      event: "Creator - Clip Shared",
      userId: clip.user?._id,
      properties: analyticsProperties,
    });
  }
};

export const trackCreatorCardShare = (card: any) => {
  analytics.track("Creator Card Shared", {
    cardId: card._id,
    badge: card.ui.badge,
    category: card.ui.category,
    cost: card.ui.cost,
    rarity: card.ui.rarity,
    uses: card.ui.uses,
    useType: card.ui.useType,
  });
};

export async function postApiSegment(data: any) {
  const opt: any = {
    method: "POST",
    url: `${process.env.REACT_APP_API_URL}/analytics/segment`,
    data: data,
  };
  try {
    return Axios(opt);
  } catch (e: any) {
    return e.response;
  }
}
